import React, { MouseEventHandler, ReactNode } from 'react';
import classNames from 'classnames';

import { OptionalEngagementProps } from '@hcs/types';

import { addOptionalEngagementProps } from '../../../../utils/engagement-props.utils';

import styles from './CircleButton.module.css';

export interface FloatingCircleButtonProps extends OptionalEngagementProps {
  /**
   * Required automation and engagement HTML attribute.
   */
  dataHcName: string;
  /**
   * Optional className applied to main HTML element.
   */
  className?: string;
  ariaLabel?: string;
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  disabled?: boolean;
  label?: ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  buttonStyle?:
    | 'primary'
    | 'secondary'
    | 'secondaryNeutral'
    | 'tertiary'
    | 'dark'
    | 'highlightYellow'
    | 'highlightGreen';
  shadow?: boolean;
  /**
   * Sets the Button `title`.
   */
  title?: React.ButtonHTMLAttributes<HTMLButtonElement>['title'];
  dataTestID?: string;
}
export const CircleButton = ({
  ariaLabel,
  dataHcName,
  className,
  children,
  dataTestID,
  onClick,
  onMouseEnter,
  onMouseLeave,
  disabled,
  label,
  size = 'md',
  buttonStyle = 'secondary',
  shadow = true,
  title,
  ...restProps
}: FloatingCircleButtonProps) => {
  return (
    <button
      data-hc-name={dataHcName}
      data-testid={dataTestID}
      {...addOptionalEngagementProps(restProps)}
      aria-label={ariaLabel || title}
      className={classNames(styles.CircleButton, className, styles[size], {
        [styles.primary]: buttonStyle === 'primary',
        [styles.secondary]: buttonStyle === 'secondary',
        [styles.secondaryNeutral]: buttonStyle === 'secondaryNeutral',
        [styles.tertiary]: buttonStyle === 'tertiary',
        [styles.dark]: buttonStyle === 'dark',
        [styles.highlightYellow]: buttonStyle === 'highlightYellow',
        [styles.highlightGreen]: buttonStyle === 'highlightGreen',
        [styles.shadow]: shadow,
        [styles.disabled]: disabled,
      })}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      title={title}
    >
      {children}
      {label && (
        <div
          data-hc-name={`${dataHcName}-label`}
          className={styles.Label}
          data-hc-event-ignore
        >
          {label}
        </div>
      )}
    </button>
  );
};
